import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bar, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { CalendarCheck, Clock, IndianRupee, LogIn, LogOut, Mail, Percent, Users } from "lucide-react";
import api, { formatINR } from "../../lib/api";
import { STATUS_LABEL, STATUS_STYLE, errorMessage, fmtDay } from "../../lib/stays";
import { useAdminData } from "../AdminContext";

const GOLD = "#C9A24B";
const CRIMSON = "#C0392B";

function Stat({ icon: Icon, label, value, sub, to }) {
  const body = (
    <div className="glass p-5 h-full hover:border-gold/30 border border-transparent transition-colors" data-testid={`stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <Icon className="text-gold mb-3" size={18} />
      <div className="font-display text-3xl text-white">{value}</div>
      <div className="text-[11px] uppercase tracking-[0.2em] text-white/40 mt-2">{label}</div>
      {sub && <div className="text-xs text-white/35 mt-1">{sub}</div>}
    </div>
  );
  return to ? <Link to={to}>{body}</Link> : body;
}

function minutesLeft(iso, now) {
  if (!iso) return null;
  return Math.max(0, Math.floor((new Date(iso).getTime() - now) / 60000));
}

function BookingRow({ b, right }) {
  return (
    <Link to={`/admin/bookings?open=${b.id}`} className="flex items-center justify-between gap-3 py-3 border-b border-white/[0.05] last:border-0 hover:bg-white/[0.02] -mx-2 px-2">
      <div className="min-w-0">
        <div className="text-white text-sm truncate">{b.guest_name || "Guest"} <span className="text-white/30 text-xs">· {b.reference}</span></div>
        <div className="text-xs text-white/40">{fmtDay(b.check_in_date, { day: "numeric", month: "short" })} → {fmtDay(b.check_out_date, { day: "numeric", month: "short" })}</div>
      </div>
      {right}
    </Link>
  );
}

function Panel({ title, icon: Icon, children, empty }) {
  const isEmpty = Array.isArray(children) ? children.length === 0 : !children;
  return (
    <section className="glass p-5">
      <h2 className="flex items-center gap-2 font-display text-lg mb-3">{Icon && <Icon size={16} className="text-gold" />} {title}</h2>
      {isEmpty ? <p className="text-sm text-white/35 py-4">{empty}</p> : children}
    </section>
  );
}

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [error, setError] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [busyId, setBusyId] = useState(null);
  const navigate = useNavigate();
  const { reloadBadges } = useAdminData();

  const load = useCallback(async () => {
    try { setD((await api.get("/admin/dashboard")).data); setError(false); } catch { setError(true); }
  }, []);
  useEffect(() => { load(); const t = setInterval(() => setNow(Date.now()), 30000); return () => clearInterval(t); }, [load]);

  const confirmPayment = async (b) => {
    if (!window.confirm(`Confirm ${b.reference} and record ${formatINR(b.total_amount)} as received?`)) return;
    setBusyId(b.id);
    try {
      await api.post(`/admin/bookings/${b.id}/confirm-payment`, { method: "upi_qr" });
      toast.success(`${b.reference} confirmed`);
      await load(); reloadBadges();
    } catch (e) { toast.error(errorMessage(e)); }
    setBusyId(null);
  };

  if (error) return <div className="glass p-8 text-center text-white/60">Couldn't load the dashboard. <button className="text-gold underline" onClick={load}>Try again</button></div>;
  if (!d) return <div className="space-y-4" aria-busy="true"><div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{[...Array(4)].map((_, i) => <div key={i} className="glass h-32 animate-pulse" />)}</div><div className="glass h-72 animate-pulse" /></div>;

  const t = d.totals;
  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl">Dashboard</h1>
        <p className="text-sm text-white/45 mt-2">{fmtDay(d.today, { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={IndianRupee} label="Revenue this month" value={formatINR(t.revenue_this_month)} sub={`${formatINR(t.revenue)} all time`} />
        <Stat icon={CalendarCheck} label="Bookings this month" value={t.bookings_this_month} sub={`${t.bookings} in total`} to="/admin/bookings" />
        <Stat icon={Percent} label="Booked, next 30 days" value={t.occupancy_next_30_days == null ? "—" : `${t.occupancy_next_30_days}%`} to="/admin/calendar" />
        <Stat icon={Users} label="Guest accounts" value={t.guests} to="/admin/guests" />
      </div>

      {(t.awaiting_payment > 0 || t.unread_messages > 0) && (
        <div className="grid md:grid-cols-2 gap-4">
          <Panel title={`Waiting for payment (${d.pending_payments.length})`} icon={Clock} empty="No bookings are waiting for payment.">
            {d.pending_payments.map((b) => {
              const left = minutesLeft(b.expires_at, now);
              return (
                <div key={b.id} className="flex items-center justify-between gap-3 py-3 border-b border-white/[0.05] last:border-0" data-testid={`pending-${b.id}`}>
                  <Link to={`/admin/bookings?open=${b.id}`} className="min-w-0 hover:text-gold">
                    <div className="text-white text-sm truncate">{b.guest_name} <span className="text-white/30 text-xs">· {b.reference}</span></div>
                    <div className="text-xs text-white/40">{formatINR(b.total_amount)}{left != null && ` · hold ends in ${left} min`}</div>
                  </Link>
                  <button disabled={busyId === b.id} onClick={() => confirmPayment(b)} className="btn-gold !py-2 !px-3 !text-[10px] shrink-0 disabled:opacity-40">Payment received</button>
                </div>
              );
            })}
          </Panel>
          <Panel title={`Unread messages (${t.unread_messages})`} icon={Mail} empty="No unread messages.">
            {t.unread_messages > 0 && <div className="py-3 text-sm text-white/60">You have {t.unread_messages} new enquiry{t.unread_messages !== 1 ? "ies" : ""}. <Link to="/admin/messages" className="text-gold underline">Read messages</Link></div>}
          </Panel>
        </div>
      )}

      <section className="glass p-5" aria-label="Last six months">
        <h2 className="font-display text-lg mb-4">Last 6 months</h2>
        <div style={{ width: "100%", height: 260 }} role="img" aria-label="Revenue and bookings for the last six months">
          <ResponsiveContainer>
            <ComposedChart data={d.series} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
              <XAxis dataKey="label" stroke="rgba(255,255,255,0.4)" tickLine={false} fontSize={12} />
              <YAxis yAxisId="rev" stroke="rgba(255,255,255,0.4)" tickLine={false} axisLine={false} fontSize={12} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} width={40} />
              <YAxis yAxisId="cnt" orientation="right" allowDecimals={false} stroke="rgba(255,255,255,0.4)" tickLine={false} axisLine={false} fontSize={12} width={28} />
              <Tooltip contentStyle={{ background: "#0E0E0E", border: "1px solid rgba(201,162,75,0.3)", color: "#fff" }} formatter={(v, n) => (n === "Revenue" ? formatINR(v) : v)} />
              <Bar yAxisId="rev" dataKey="revenue" name="Revenue" fill={GOLD} radius={[2, 2, 0, 0]} maxBarSize={44} />
              <Line yAxisId="cnt" type="monotone" dataKey="bookings" name="Bookings" stroke={CRIMSON} strokeWidth={2} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <div className="flex gap-5 text-xs text-white/50 mt-3"><span className="flex items-center gap-2"><i className="w-3 h-3 inline-block" style={{ background: GOLD }} /> Revenue received</span><span className="flex items-center gap-2"><i className="w-3 h-0.5 inline-block" style={{ background: CRIMSON }} /> Bookings</span></div>
      </section>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        <Panel title="Checking in today" icon={LogIn} empty="Nobody checks in today.">
          {d.todays_checkins.map((b) => <BookingRow key={b.id} b={b} right={<span className={`text-[10px] uppercase tracking-[0.15em] border px-2 py-1 ${STATUS_STYLE[b.status]}`}>{STATUS_LABEL[b.status]}</span>} />)}
        </Panel>
        <Panel title="Checking out today" icon={LogOut} empty="Nobody checks out today.">
          {d.todays_checkouts.map((b) => <BookingRow key={b.id} b={b} right={<span className={`text-[10px] uppercase tracking-[0.15em] border px-2 py-1 ${STATUS_STYLE[b.status]}`}>{STATUS_LABEL[b.status]}</span>} />)}
        </Panel>
        <Panel title="Arriving in the next 7 days" icon={CalendarCheck} empty="No confirmed arrivals this week.">
          {d.upcoming.map((b) => <BookingRow key={b.id} b={b} right={<span className="text-gold text-sm">{formatINR(b.total_amount)}</span>} />)}
        </Panel>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <section className="glass p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-3"><h2 className="font-display text-lg">Recent bookings</h2><button onClick={() => navigate("/admin/bookings")} className="text-xs text-gold">View all</button></div>
          {d.recent.length === 0 ? <p className="text-sm text-white/35 py-4">No bookings yet.</p> : d.recent.map((b) => (
            <BookingRow key={b.id} b={b} right={<div className="text-right"><div className="text-gold text-sm">{formatINR(b.total_amount)}</div><span className={`text-[10px] uppercase tracking-[0.15em] ${STATUS_STYLE[b.status]?.split(" ")[0]}`}>{STATUS_LABEL[b.status]}</span></div>} />
          ))}
        </section>
        <section className="glass p-5">
          <h2 className="font-display text-lg mb-3">Bookings by status</h2>
          <div className="flex flex-wrap gap-2">
            {Object.entries(d.status_counts).length === 0 && <span className="text-sm text-white/35">Nothing yet</span>}
            {Object.entries(d.status_counts).map(([s, n]) => (
              <Link key={s} to={`/admin/bookings?status=${s}`} className={`text-xs border px-3 py-2 ${STATUS_STYLE[s] || "border-white/20 text-white/60"}`}>{STATUS_LABEL[s] || s}: <b>{n}</b></Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
